<template>
  <v-row>
    <v-col cols="12">
      <v-card class="txn-card" flat>
        <!-- HEADER BAND -->
        <div class="txn-header pl-base-bg">
          <div class="txn-header-icon">
            <v-icon color="#fff" size="22">mdi-swap-horizontal</v-icon>
          </div>
          <div>
            <div class="txn-title">Create Transaction</div>
            <div class="txn-subtitle">Record a new income or expense</div>
          </div>
        </div>

        <!-- FORM -->
        <v-form ref="form" v-model="valid" class="pa-6">
          <div class="txn-section-label">Transaction Details</div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.customerName"
                label="Customer / Vendor Name"
                outlined
                dense
                prepend-inner-icon="mdi-account"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.amount"
                label="Amount"
                type="number"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-currency-inr"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-select
                v-model="form.type"
                :items="types"
                item-text="text"
                item-value="value"
                label="Type"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-format-list-bulleted"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.category"
                label="Category"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-tag"
              />
            </v-col>
          </v-row>

          <div class="txn-section-label mt-2">Payment &amp; Notes</div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.paymentMethod"
                :items="paymentMethods"
                item-text="text"
                item-value="value"
                label="Payment Method"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-credit-card"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formattedDate"
                    label="Date"
                    readonly
                    outlined
                    dense
                    :rules="[rules.required]"
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>

                <v-date-picker v-model="form.date" @change="handleDateChange" />
              </v-menu>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="form.note"
                label="Note"
                outlined
                dense
                rows="2"
                prepend-inner-icon="mdi-note-text"
              />
            </v-col>
          </v-row>

          <!-- ACTIONS -->
          <v-row class="mt-4" justify="end" dense>
            <v-col cols="6" sm="auto">
              <v-btn block color="secondary" @click="reset">Reset</v-btn>
            </v-col>

            <v-col v-if="!isViewOnly" cols="6" sm="auto">
              <v-btn
                block
                class="txn-save-btn"
                :loading="loading"
                @click="submit"
              >
                Save
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "CreateTransactions",

  data() {
    const getISTDate = () => {
      return new Date().toLocaleDateString("en-CA", {
        timeZone: "Asia/Kolkata",
      });
    };
    return {
      valid: false,
      loading: false,

      dateMenu: false,

      form: {
        customerName: "",
        amount: "",
        type: "EXPENSE",
        category: "",
        paymentMethod: "CASH",
        date: getISTDate(),
        note: "",
      },

      types: [
        { text: "Income", value: "INCOME" },
        { text: "Expense", value: "EXPENSE" },
      ],

      paymentMethods: [
        { text: "Cash", value: "CASH" },
        { text: "Bank Transfer", value: "BANK_TRANSFER" },
        { text: "UPI", value: "UPI" },
        { text: "Debit Card", value: "DEBIT_CARD" },
        { text: "Credit Card", value: "CREDIT_CARD" },
        { text: "Cheque", value: "CHEQUE" },
        { text: "Net Banking", value: "NET_BANKING" },
        { text: "Wallet", value: "WALLET" },
        { text: "Other", value: "OTHER" },
      ],

      rules: {
        required: (v) => !!v || "Required",
      },
    };
  },

  computed: {
    formattedDate() {
      if (!this.form.date) return "";

      const [year, month, day] = this.form.date.split("-");
      return `${day}-${month}-${year}`;
    },
  },

  methods: {
    ...mapActions("transactions", ["createTransaction"]),

    handleDateChange(val) {
      this.form.date = val;
      this.dateMenu = false;
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      try {
        const payload = {
          customerName: this.form.customerName,
          amount: this.form.amount ? Number(this.form.amount) : 0,
          type: this.form.type,
          category: this.form.category || "",
          paymentMethod: this.form.paymentMethod,
          date: this.form.date,
          note: this.form.note || "",
        };

        let res = await this.createTransaction(payload);

        this.$toast?.success("Transaction added successfully");

        this.reset();

        console.log("Transaction creation response:", res);
        let transaction = res?.data?.transaction;
        if (transaction) {
          this.$router.push(`/transactions/${transaction._id}/view`);
        }
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to add transaction",
        );
      } finally {
        this.loading = false;
      }
    },

    reset() {
      this.$refs.form.reset();

      this.form.type = "EXPENSE";
      this.form.paymentMethod = "CASH";
      this.form.date = new Date().toLocaleDateString("en-CA", {
        timeZone: "Asia/Kolkata",
      });
    },
  },
};
</script>

<style scoped>
.txn-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.txn-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.txn-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.txn-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.txn-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.txn-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.txn-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

/* prevent overlap issues */
.v-menu__content {
  z-index: 9999 !important;
}
</style>
